import { Query } from "interfaces/api.interface";
import { FetchQuotesResponseDto } from "./dtos/fetch-quotes.response.dto";
import { IQuoteJob, IQuotedRate } from "models/quote/quote.model";
import {
  FetchSingleExternalQuoteResponseDto,
  FetchSingleQuoteResponseDto,
  FetchGenerateQuoteReference,
  ValidateQuoteReference,
  hasTrackingAvailableDto,
} from "./dtos/fetch-single-quote-response.dto";
import { fetchAll, fetchSingle } from "services/get";
import { apiAdminPaths, apiPaths, customerApiBase } from "enum/paths.enum";
import { post } from "services/post";
import { fetcher, IFetchPostResponse, IFetchResponseSingle } from "services/api";
import { ICreateQuoteRequestDto } from "./dtos/create-quote.request.dto";
import { AdminCreateQuoteRequestDto } from "./dtos/admin-create-quote.request.dto";
import { ISalesAccount } from "models/salesAccount/salesAccount.model";
import { CreateQuoteResponseDto } from "./dtos/create-quote.response.dto";
import { DownloadQuotesRequestDto } from "./dtos/download-quotes.request.dto";
import { ISupplier } from "models/supplier/supplier.model";
import {
  LandedCostRequestDto,
  LandedCostResponse,
} from "./dtos/landed-cost.dto";
import {
  DHLPickupCapabilitiesType,
  DHLServicePoint,
  DHLValueAddedServiceOption,
  DropOffAddress,
} from "store/createShipmentSlice/createShipmentSlice.types";

export const fetchQuotes = async (
  salesAccountUUID: ISalesAccount["uuid"],
  query?: Query<IQuoteJob, { search?: string }>
): Promise<FetchQuotesResponseDto> => {
  return await fetchAll(
    customerApiBase + "/" + salesAccountUUID + apiPaths.quotes,
    query
  );
};

export const fetchAdminQuotes = async (
  query?: Query<IQuoteJob, { search?: string }>
): Promise<FetchQuotesResponseDto> => {
  return await fetchAll(apiAdminPaths.quotes, query);
};

export const createQuote = async (
  salesAccountUUID: ISalesAccount["uuid"],
  values: ICreateQuoteRequestDto
): Promise<CreateQuoteResponseDto> => {
  return await post(
    customerApiBase + "/" + salesAccountUUID + apiPaths.quotes,
    values
  );
};

export const createAdminQuote = async (
  values: AdminCreateQuoteRequestDto
): Promise<IFetchPostResponse<IQuoteJob>> => {
  return await post(apiAdminPaths.quotes, values);
};

export const createLiabilityQuote = async (
  salesAccountUUID: ISalesAccount["uuid"],
  values: ICreateQuoteRequestDto
): Promise<CreateQuoteResponseDto> => {
  return await post(
    customerApiBase + "/" + salesAccountUUID + apiPaths.quotes + "/externalLiability",
    values
  );
};

// DHL Express value added services purchasable on this rate's lane, already priced.
export const fetchDHLValueAddedServices = async (
  salesAccountUUID: ISalesAccount["uuid"],
  rateId: IQuotedRate["id"]
): Promise<
  IFetchResponseSingle<{ valueAddedServices: DHLValueAddedServiceOption[] }>
> => {
  return await fetchSingle(
    customerApiBase +
      "/" +
      salesAccountUUID +
      apiPaths.quotes +
      "/rates/" +
      rateId +
      "/valueAddedServices"
  );
};

export type ApplyValueAddedServicesRequestDto = {
  rateId: IQuotedRate["id"];
  valueAddedServices: string[];
};

export type ApplyValueAddedServicesResponse = IFetchPostResponse<{
  totalSalesSurcharges: number;
  netChargeSales: number;
  appliedServices: DHLValueAddedServiceOption[];
  // DHL accepted these but quoted no separate charge for them, so nothing was added.
  unpricedServiceCodes: string[];
}>;

// Re-rates with the selected DHL value added services and stores their charges on the
// rate - the DHL sibling of createLiabilityQuote.
export const createValueAddedServicesQuote = async (
  salesAccountUUID: ISalesAccount["uuid"],
  values: ApplyValueAddedServicesRequestDto
): Promise<ApplyValueAddedServicesResponse> => {
  return await post(
    customerApiBase +
      "/" +
      salesAccountUUID +
      apiPaths.quotes +
      "/externalValueAddedServices",
    values
  );
};

export const fetchDHLPickupCapabilities = async (
  salesAccountUUID: ISalesAccount["uuid"],
  rateId: IQuotedRate["id"]
): Promise<IFetchResponseSingle<{ pickupCapabilities: DHLPickupCapabilitiesType | null }>> => {
  return await fetchSingle(
    customerApiBase +
      "/" +
      salesAccountUUID +
      apiPaths.quotes +
      "/rates/" +
      rateId +
      "/pickupCapabilities"
  );
};

// DHL's drop-off locator, the sibling of fetchShipmentDropOffLocationsUPS. The rate id
// picks the DHL account the search runs on, so this one hangs off the rate rather than
// off the shipments endpoints.
export const fetchDHLServicePoints = async (
  salesAccountUUID: ISalesAccount["uuid"],
  rateId: IQuotedRate["id"],
  address: DropOffAddress
): Promise<IFetchResponseSingle<{ servicePoints: DHLServicePoint[] }>> => {
  return await post(
    customerApiBase +
      "/" +
      salesAccountUUID +
      apiPaths.quotes +
      "/rates/" +
      rateId +
      "/servicePoints",
    address
  );
};

// Estimated import duty, tax and customs charges for a UPS or DHL Express export
// rate. Hangs off the rate like fetchDHLServicePoints: the rate supplies the lane and
// the quoted freight and proves the caller owns it, so the body carries only the goods.
export const fetchLandedCost = async (
  salesAccountUUID: ISalesAccount["uuid"],
  rateId: IQuotedRate["id"],
  values: LandedCostRequestDto
): Promise<IFetchPostResponse<LandedCostResponse>> => {
  return await post(
    customerApiBase +
      "/" +
      salesAccountUUID +
      apiPaths.quotes +
      "/rates/" +
      rateId +
      "/landedCost",
    values
  );
};

export const fetchQuote = async (
  salesAccountUUID: ISalesAccount["uuid"],
  uuid: IQuoteJob["uuid"]
): Promise<FetchSingleQuoteResponseDto> => {
  return await fetchSingle(
    customerApiBase + "/" + salesAccountUUID + apiPaths.quotes + "/" + uuid
  );
};

export const fetchGeneratedReference = async (
  salesAccountUUID: ISalesAccount["uuid"]
): Promise<FetchGenerateQuoteReference> => {
  return await fetchSingle(
    customerApiBase + "/" + salesAccountUUID + apiPaths.quotes + "/generate"
  );
};

export const validateJobNumber = async (
  salesAccountUUID: ISalesAccount["uuid"],
  values?: { jobNumber?: string }
): Promise<ValidateQuoteReference> => {
  return await post(
    customerApiBase +
      "/" +
      salesAccountUUID +
      apiPaths.quotes +
      "/checkJobNumberAvailable",
    values
  );
};

export const fetchExternalQuote = async (
  salesAccountUUID: ISalesAccount["uuid"],
  id: IQuoteJob["id"]
): Promise<FetchSingleExternalQuoteResponseDto> => {
  return await fetchAll(
    customerApiBase +
      "/" +
      salesAccountUUID +
      apiPaths.quotes +
      "/external/" +
      id
  );
};
export const fetchIfTrackingAvailable = async (
  salesAccountUUID: ISalesAccount["uuid"],
  supplier : ISupplier["id"]
): Promise<hasTrackingAvailableDto> => {
  return await fetchSingle(
    customerApiBase +
      "/" +
      salesAccountUUID +
      apiPaths.quotes +
      "/trackingAvailability/" +
      supplier
  );
};
export const fetchAdminQuote = async (
  jobNumber: IQuoteJob["jobNumber"]
): Promise<FetchSingleQuoteResponseDto> => {
  return await fetchSingle(apiAdminPaths.quotes + "/" + jobNumber);
};

export const sendQuoteEmail = async (values: {
  quoteJobUUID: IQuoteJob["uuid"];
  emails: string[];
  selectedRates: IQuotedRate["uuid"][];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
}): Promise<IFetchPostResponse<any>> => {
  return await fetcher({
    method: "POST",
    url: apiAdminPaths.sendQuotesEmail,
    body: values,
  });
};

export const downloadQuotesReport = async (
  values?: DownloadQuotesRequestDto
): Promise<IFetchPostResponse<string>> => {
  return await post(apiAdminPaths.quotes + "/downloadQuotesReport", values);
};
